/**
 * Ritual Bundle Builder
 * Crown Affair-style freeform product picker + Loop Subscriptions integration
 */
(function() {
  'use strict';

  const LOOP_BUNDLE_ID = '01KGEVE1PY0SEZAXYGZSRGMZ0S';
  const LOOP_API_BASE = 'https://api.loopsubscriptions.com/storefront/2023-10';
  const MONEY_FORMAT = '${{amount}}';
  const SUBSCRIPTION_DISCOUNT_PERCENT = 10;
  const ONETIME_ONLY_PRODUCT_IDS = []; // Gummies (8294447251635) now has selling plans — no longer one-time-only. hideOnetimeOnlyCards() below is a no-op with an empty list.

  class RitualBundleBuilder {
    constructor() {
      this.state = {
        purchaseMode: 'recurring',       // 'recurring' | 'onetime'
        frequency: '3_MONTH',
        sellingPlanShopifyId: null,
        selectedItems: new Map(),         // variantId -> { variantId, productId, handle, title, variantTitle, price, comparePrice, quantity, imageUrl, isAccessory }
        accessoryItems: new Map(),        // variantId -> same shape
        currentStep: 1,
        loopTxnId: null,
        loopBundle: null,                 // cached Loop bundle response
        isLoading: false,
        isSubmitting: false
      };

      this.init();
    }

    async init() {
      this.cacheDOM();
      this.bindEvents();
      this.initMobileBarVisibility();
      this.hideOnetimeOnlyCards();
      await this.fetchLoopBundle();
      this.updateUI();
    }

    hideOnetimeOnlyCards() {
      const hide = this.state.purchaseMode === 'recurring';
      this.allCards.forEach(card => {
        if (ONETIME_ONLY_PRODUCT_IDS.includes(card.dataset.productId)) {
          card.style.display = hide ? 'none' : '';
        }
      });
    }

    /* ========== DOM Caching ========== */
    cacheDOM() {
      // Toggle
      this.toggleButtons = document.querySelectorAll('.rb-toggle__btn');
      this.frequencyRow = document.querySelector('.rb-toggle__btn-freq');
      this.frequencySelect = document.getElementById('rb-frequency-select');

      // Cards
      this.allCards = document.querySelectorAll('.rb-card:not([data-is-accessory])');
      this.accessoryCards = document.querySelectorAll('.rb-card[data-is-accessory]');

      // Sidebar desktop
      this.sidebarItems = document.getElementById('rb-sidebar-items');
      this.sidebarEmpty = document.getElementById('rb-sidebar-empty');
      this.sidebarTotals = document.getElementById('rb-sidebar-totals');
      this.sidebarSubtotal = document.getElementById('rb-sidebar-subtotal');
      this.sidebarCompare = document.getElementById('rb-sidebar-compare');
      this.sidebarCompareRow = document.getElementById('rb-sidebar-compare-row');
      this.sidebarSavings = document.getElementById('rb-sidebar-savings');
      this.sidebarSavingsRow = document.getElementById('rb-sidebar-savings-row');
      this.sidebarSubRow = document.getElementById('rb-sidebar-sub-row');
      this.sidebarSubSavings = document.getElementById('rb-sidebar-sub-savings');
      this.sidebarTotal = document.getElementById('rb-sidebar-total');
      this.sidebarCta = document.getElementById('rb-sidebar-cta');

      // Tier progress
      this.tiersContainer = document.getElementById('rb-sidebar-tiers');
      this.tiersFill = document.getElementById('rb-sidebar-tiers-fill');
      this.tiersMarkers = document.getElementById('rb-sidebar-tiers-markers');
      this.tiersMsg = document.getElementById('rb-sidebar-tiers-msg');

      // Mobile bar
      this.mobileBar = document.getElementById('rb-mobile-bar');
      this.mobileCount = document.getElementById('rb-mobile-count');
      this.mobileTotal = document.getElementById('rb-mobile-total');
      this.mobileCta = document.getElementById('rb-mobile-cta');
      this.mobileExpand = document.getElementById('rb-mobile-expand');

      // Step 2
      this.step1Sections = document.querySelectorAll('[data-step="1"]');
      this.step2Sections = document.querySelectorAll('[data-step="2"]');
      this.checkoutBtn = document.getElementById('rb-checkout-btn');
      this.skipAccessoriesBtn = document.getElementById('rb-skip-accessories');

      // Sections for step toggle
      this.sidebarToggle = document.querySelector('.rb-sidebar__toggle');
      this.categorySections = document.querySelectorAll('.rb-category-section');
      this.accessoriesSection = document.querySelector('.rb-accessories-section');
    }

    /* ========== Event Binding ========== */
    bindEvents() {
      // All interactions via event delegation for mobile reliability
      document.addEventListener('click', (e) => {
        // Purchase toggle
        const toggleBtn = e.target.closest('.rb-toggle__btn');
        if (toggleBtn && toggleBtn.dataset.mode) {
          e.preventDefault();
          this.setPurchaseMode(toggleBtn.dataset.mode);
          return;
        }

        // Product card interactions
        const addBtn = e.target.closest('.rb-card__add-btn');
        const minusBtn = e.target.closest('.rb-card__qty-btn--minus');
        const plusBtn = e.target.closest('.rb-card__qty-btn--plus');
        const removeBtn = e.target.closest('.rb-sidebar__item-remove');

        if (addBtn) {
          const card = addBtn.closest('.rb-card');
          if (card) this.addItem(card);
        } else if (minusBtn) {
          const card = minusBtn.closest('.rb-card');
          if (card) this.decrementItem(card);
        } else if (plusBtn) {
          const card = plusBtn.closest('.rb-card');
          if (card) this.incrementItem(card);
        } else if (removeBtn) {
          const variantId = removeBtn.dataset.variantId;
          const isAccessory = removeBtn.dataset.isAccessory === 'true';
          if (variantId) this.removeItemById(variantId, isAccessory);
          return;
        }

        // Sidebar CTA
        if (e.target.closest('#rb-sidebar-cta')) {
          this.handleCtaClick();
          return;
        }

        // Mobile CTA
        if (e.target.closest('#rb-mobile-cta')) {
          this.handleCtaClick();
          return;
        }

        // Mobile expand - scroll to sidebar
        if (e.target.closest('#rb-mobile-expand')) {
          this.scrollToSidebar();
          return;
        }

        // Step 2 buttons
        if (e.target.closest('#rb-checkout-btn')) {
          this.handleCheckout();
          return;
        }
        if (e.target.closest('#rb-skip-accessories')) {
          this.handleCheckout();
          return;
        }
      });

      // Variant selector & frequency changes via delegation
      document.addEventListener('change', (e) => {
        if (e.target.classList.contains('rb-card__variant-select')) {
          const card = e.target.closest('.rb-card');
          if (card) this.handleVariantChange(card, e.target);
        }
        if (e.target.id === 'rb-frequency-select') {
          this.state.frequency = e.target.value;
          this.matchSellingPlan();
          this.updateUI();
        }
      });
    }

    /* ========== Loop API ========== */
    async fetchLoopBundle() {
      try {
        this.state.isLoading = true;
        const res = await fetch(`${LOOP_API_BASE}/bundle/${LOOP_BUNDLE_ID}`);
        if (!res.ok) {
          console.warn('[RitualBundle] Loop API returned', res.status, '- falling back to basic mode');
          return;
        }
        const json = await res.json();
        const data = json.data || json;
        this.state.loopBundle = data;
        this.matchSellingPlan();
        this.hydrateStockStatus(data);
        console.log('[RitualBundle] Loop bundle loaded', data);
      } catch (err) {
        console.warn('[RitualBundle] Failed to fetch Loop bundle:', err);
      } finally {
        this.state.isLoading = false;
      }
    }

    matchSellingPlan() {
      const bundle = this.state.loopBundle;
      if (!bundle || !bundle.sellingPlans) return;

      if (this.state.purchaseMode === 'onetime') {
        this.state.sellingPlanShopifyId = null;
        return;
      }

      // Parse frequency: "3_MONTH" -> { count: 3, interval: "MONTH" }
      const [countStr, interval] = this.state.frequency.split('_');
      const count = parseInt(countStr, 10);

      // Try billingPolicy first, fall back to parsing selling plan name
      const match = bundle.sellingPlans.find(sp => {
        if (sp.billingPolicy) {
          return sp.billingPolicy.intervalCount === count &&
            sp.billingPolicy.interval === interval;
        }
        // Parse name like "Deliver every 3 months"
        const nameMatch = sp.name && sp.name.match(/every\s+(\d+)\s+month/i);
        return nameMatch && parseInt(nameMatch[1], 10) === count;
      });

      if (match) {
        this.state.sellingPlanShopifyId = match.shopifyId;
      } else if (bundle.sellingPlans.length > 0) {
        this.state.sellingPlanShopifyId = bundle.sellingPlans[0].shopifyId;
      }
    }

    hydrateStockStatus(data) {
      if (!data || !data.products) return;

      // Build a map of variant stock from Loop data
      const stockMap = new Map();
      data.products.forEach(p => {
        if (p.variants) {
          p.variants.forEach(v => {
            stockMap.set(String(v.shopifyId), v.available !== false);
          });
        }
      });

      // Update card availability
      this.allCards.forEach(card => {
        const variantId = card.dataset.variantId;
        if (stockMap.has(variantId) && !stockMap.get(variantId)) {
          card.dataset.available = 'false';
          card.classList.add('rb-card--soldout');
          const btn = card.querySelector('.rb-card__add-btn');
          if (btn) {
            btn.disabled = true;
            const label = btn.querySelector('.rb-card__add-label');
            if (label) label.textContent = 'Sold Out';
          }
        }
      });
    }

    getDiscount() {
      const bundle = this.state.loopBundle;
      if (!bundle || !bundle.discounts || bundle.discounts.length === 0) return null;

      const itemCount = this.getTotalItemCount();

      // Find the best applicable discount tier
      // Loop API uses minCartQuantity for the item threshold
      let bestDiscount = null;
      for (const d of bundle.discounts) {
        const min = d.minCartQuantity || d.minItems || 0;
        if (min && itemCount >= min) {
          const bestMin = bestDiscount ? (bestDiscount.minCartQuantity || bestDiscount.minItems || 0) : 0;
          if (min > bestMin) {
            bestDiscount = d;
          }
        }
      }

      // If no tier matched, return null (no discount yet)
      return bestDiscount || null;
    }

    getDiscountPercent() {
      const discount = this.getDiscount();
      if (!discount) return 0;
      if (discount.type === 'PERCENTAGE' && discount.value) return discount.value;
      return 0;
    }

    async applyBundleDiscountCode() {
      const discount = this.getDiscount();
      if (!discount || !discount.code) return;

      try {
        await fetch('/discount/' + encodeURIComponent(discount.code), {
          method: 'GET'
        });
        console.log('[RitualBundle] Applied discount code:', discount.code);
      } catch (err) {
        console.warn('[RitualBundle] Failed to apply discount code:', err);
      }
    }

    async createTransaction() {
      const bundle = this.state.loopBundle;
      const items = Array.from(this.state.selectedItems.values());
      if (items.length === 0) return null;

      const productVariants = items.map(item => ({
        shopifyId: parseInt(item.variantId, 10),
        quantity: item.quantity
      }));

      // Pick the right boxSize if available
      let boxSizeId = null;
      if (bundle && bundle.boxSizes && bundle.boxSizes.length > 0) {
        const totalQty = items.reduce((sum, i) => sum + i.quantity, 0);
        // Find matching box size (RANGE type uses minSize/maxSize)
        const match = bundle.boxSizes.find(bs => {
          const min = bs.minSize || bs.size || 0;
          const max = bs.maxSize || Infinity;
          return totalQty >= min && (max === 0 || totalQty <= max);
        });
        boxSizeId = match ? match.id : bundle.boxSizes[0].id;
      }

      const discount = this.getDiscount();

      const body = {
        bundleId: LOOP_BUNDLE_ID,
        productVariants,
        quantity: 1
      };

      if (boxSizeId) body.boxSizeId = boxSizeId;
      if (discount && discount.id) body.discountId = discount.id;
      if (this.state.sellingPlanShopifyId) body.sellingPlanShopifyId = this.state.sellingPlanShopifyId;

      const res = await fetch(`${LOOP_API_BASE}/bundle/${LOOP_BUNDLE_ID}/transaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Loop transaction failed (${res.status}): ${errText}`);
      }

      const data = await res.json();
      this.state.loopTxnId = data.txnId || data.id;
      return this.state.loopTxnId;
    }

    /* ========== Item Management ========== */
    getCardData(card) {
      const variantId = card.dataset.variantId;
      const productId = card.dataset.productId;
      const handle = card.dataset.productHandle;
      const price = parseInt(card.dataset.price, 10);
      const comparePrice = parseInt(card.dataset.comparePrice, 10) || price;
      const isAccessory = card.dataset.isAccessory === 'true';
      const title = card.querySelector('.rb-card__title')?.textContent?.trim()
        || card.querySelector('.rb-card__title-bold')?.textContent?.trim()
        || card.querySelector('.rb-card__title-lines')?.textContent?.trim()
        || '';
      const variantTitle = card.querySelector('.rb-card__variant-select')?.selectedOptions?.[0]?.textContent?.trim() || '';
      const imageUrl = card.querySelector('.rb-card__img')?.src || '';

      const hasSellingPlans = card.dataset.hasSellingPlans === 'true';
      // The plans THIS variant can actually take. hasSellingPlans only says the product
      // has some plan somewhere, which is a different question.
      const sellingPlanIds = (card.dataset.sellingPlanIds || '')
        .split(',')
        .map(id => parseInt(id, 10))
        .filter(id => id > 0);

      return { variantId, productId, handle, title, variantTitle, price, comparePrice, quantity: 1, imageUrl, isAccessory, hasSellingPlans, sellingPlanIds };
    }

    addItem(card) {
      const data = this.getCardData(card);
      const map = data.isAccessory ? this.state.accessoryItems : this.state.selectedItems;

      if (map.has(data.variantId)) {
        const existing = map.get(data.variantId);
        existing.quantity += 1;
      } else {
        map.set(data.variantId, data);
      }

      card.classList.add('rb-card--selected');
      this.updateCardQtyDisplay(card, map.get(data.variantId).quantity);
      this.updateUI();
      this.emitEvent('rb:itemAdded', data);
    }

    decrementItem(card) {
      const variantId = card.dataset.variantId;
      const isAccessory = card.dataset.isAccessory === 'true';
      const map = isAccessory ? this.state.accessoryItems : this.state.selectedItems;

      const item = map.get(variantId);
      if (!item) return;

      item.quantity -= 1;
      if (item.quantity <= 0) {
        map.delete(variantId);
        card.classList.remove('rb-card--selected');
        this.updateCardQtyDisplay(card, 0);
      } else {
        this.updateCardQtyDisplay(card, item.quantity);
      }

      this.updateUI();
      this.emitEvent('rb:itemRemoved', { variantId });
    }

    incrementItem(card) {
      const variantId = card.dataset.variantId;
      const isAccessory = card.dataset.isAccessory === 'true';
      const map = isAccessory ? this.state.accessoryItems : this.state.selectedItems;

      const item = map.get(variantId);
      if (!item) return;

      item.quantity += 1;
      this.updateCardQtyDisplay(card, item.quantity);
      this.updateUI();
    }

    removeItemById(variantId, isAccessory) {
      const map = isAccessory ? this.state.accessoryItems : this.state.selectedItems;
      map.delete(variantId);

      // Update card state
      const selector = `[data-variant-id="${variantId}"]${isAccessory ? '[data-is-accessory]' : ':not([data-is-accessory])'}`;
      const card = document.querySelector(selector);
      if (card) {
        card.classList.remove('rb-card--selected');
        this.updateCardQtyDisplay(card, 0);
      }

      this.updateUI();
      this.emitEvent('rb:itemRemoved', { variantId });
    }

    handleVariantChange(card, select) {
      const oldVariantId = card.dataset.variantId;
      const newVariantId = select.value;
      const option = select.selectedOptions[0];
      const isAccessory = card.dataset.isAccessory === 'true';
      const map = isAccessory ? this.state.accessoryItems : this.state.selectedItems;

      // Update card data attributes
      card.dataset.variantId = newVariantId;
      card.dataset.price = option.dataset.price;
      card.dataset.comparePrice = option.dataset.comparePrice;
      card.dataset.available = option.dataset.available;
      card.dataset.sellingPlanIds = option.dataset.sellingPlanIds || '';

      // Update add button price
      const addLabel = card.querySelector('.rb-card__add-label');
      if (addLabel && option.dataset.available === 'true') {
        addLabel.textContent = `+ Add ${this.formatMoney(parseInt(option.dataset.price, 10))}`;
      }

      // If this item was selected, transfer to new variant
      if (map.has(oldVariantId)) {
        const existing = map.get(oldVariantId);
        map.delete(oldVariantId);
        existing.variantId = newVariantId;
        existing.price = parseInt(option.dataset.price, 10);
        existing.comparePrice = parseInt(option.dataset.comparePrice, 10) || existing.price;
        existing.variantTitle = option.textContent.trim();
        map.set(newVariantId, existing);
        this.updateUI();
      }
    }

    updateCardQtyDisplay(card, qty) {
      const qtyValue = card.querySelector('.rb-card__qty-value');
      if (qtyValue) qtyValue.textContent = qty;

      const addBtn = card.querySelector('.rb-card__add-btn');
      const qtyControls = card.querySelector('.rb-card__qty-controls');

      if (qty > 0) {
        if (addBtn) addBtn.style.display = 'none';
        if (qtyControls) qtyControls.style.display = 'flex';
      } else {
        if (addBtn) addBtn.style.display = '';
        if (qtyControls) qtyControls.style.display = 'none';
      }
    }

    /* ========== UI Updates ========== */
    updateUI() {
      this.renderSidebar();
      this.renderTierProgress();
      this.updateCardPrices();
      this.updateMobileBar();
      this.updateCtaState();
    }

    renderSidebar() {
      if (!this.sidebarItems) return;

      const items = Array.from(this.state.selectedItems.values());
      const accessories = Array.from(this.state.accessoryItems.values());
      const allItems = [...items, ...accessories];

      // Total number of slots (expand with quantity, max 8)
      const MAX_SLOTS = 8;
      const filledSlots = [];
      allItems.forEach(item => {
        for (let i = 0; i < item.quantity; i++) {
          filledSlots.push(item);
        }
      });

      // Build the grid
      let gridEl = this.sidebarItems.querySelector('.rb-sidebar__item-grid');
      if (!gridEl) {
        gridEl = document.createElement('div');
        gridEl.className = 'rb-sidebar__item-grid';
        this.sidebarItems.innerHTML = '';
        this.sidebarItems.appendChild(gridEl);
      }

      gridEl.innerHTML = '';

      const totalSlots = Math.max(MAX_SLOTS, filledSlots.length);
      for (let i = 0; i < totalSlots; i++) {
        const slot = document.createElement('div');
        const item = filledSlots[i];

        if (item) {
          slot.className = 'rb-sidebar__slot rb-sidebar__slot--filled';
          slot.innerHTML = `
            ${item.imageUrl ? `<img class="rb-sidebar__slot-img" src="${item.imageUrl}" alt="${this.escapeHtml(item.title)}">` : ''}
            <button type="button" class="rb-sidebar__slot-remove rb-sidebar__item-remove" data-variant-id="${item.variantId}" data-is-accessory="${item.isAccessory}" aria-label="Remove ${this.escapeHtml(item.title)}">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
            <span class="rb-sidebar__slot-name">${this.escapeHtml(item.title)}</span>
            ${item.quantity > 1 ? `<span class="rb-sidebar__slot-qty">${item.quantity}</span>` : ''}
          `;
        } else {
          slot.className = 'rb-sidebar__slot rb-sidebar__slot--empty';
          slot.innerHTML = `<span class="rb-sidebar__slot-label">Select<br>Product</span>`;
        }

        gridEl.appendChild(slot);
      }

      // Totals
      this.updateTotals(items, accessories);
    }

    renderTierProgress() {
      const bundle = this.state.loopBundle;
      if (!bundle || !bundle.discounts || bundle.discounts.length === 0) {
        if (this.tiersContainer) this.tiersContainer.style.display = 'none';
        return;
      }

      if (this.tiersContainer) this.tiersContainer.style.display = '';

      // Normalize tiers: use minCartQuantity or minItems
      const tiers = [...bundle.discounts]
        .map(d => ({ ...d, min: d.minCartQuantity || d.minItems || 0 }))
        .filter(d => d.min > 0 && d.type === 'PERCENTAGE')
        .sort((a, b) => a.min - b.min);

      if (tiers.length === 0) {
        if (this.tiersContainer) this.tiersContainer.style.display = 'none';
        return;
      }

      const maxItems = tiers[tiers.length - 1].min;
      const itemCount = this.getTotalItemCount();

      // Render markers (only once, then update classes)
      if (this.tiersMarkers && !this.tiersMarkers.dataset.rendered) {
        this.tiersMarkers.innerHTML = '';
        tiers.forEach(tier => {
          const pct = (tier.min / maxItems) * 100;
          const marker = document.createElement('div');
          marker.className = 'rb-sidebar__tier-marker';
          marker.style.left = pct + '%';
          marker.dataset.min = tier.min;
          marker.innerHTML = `
            <span class="rb-sidebar__tier-dot"></span>
            <span class="rb-sidebar__tier-label">${tier.min}+ items<br>${tier.value}% off</span>
          `;
          this.tiersMarkers.appendChild(marker);
        });
        this.tiersMarkers.dataset.rendered = 'true';
      }

      // Update fill bar
      const fillPct = Math.min((itemCount / maxItems) * 100, 100);
      if (this.tiersFill) this.tiersFill.style.width = fillPct + '%';

      // Update marker states
      if (this.tiersMarkers) {
        this.tiersMarkers.querySelectorAll('.rb-sidebar__tier-marker').forEach(marker => {
          const min = parseInt(marker.dataset.min, 10);
          const reached = itemCount >= min;
          marker.querySelector('.rb-sidebar__tier-dot').classList.toggle('rb-sidebar__tier-dot--reached', reached);
          marker.querySelector('.rb-sidebar__tier-label').classList.toggle('rb-sidebar__tier-label--reached', reached);
        });
      }

      // Update message
      if (this.tiersMsg) {
        const currentTier = this.getDiscount();
        const nextTier = tiers.find(t => t.min > itemCount);

        const subNote = this.state.purchaseMode === 'recurring' ? ` + ${SUBSCRIPTION_DISCOUNT_PERCENT}% subscriber discount` : '';

        if (itemCount === 0) {
          this.tiersMsg.textContent = `Add ${tiers[0].min} items to unlock ${tiers[0].value}% off${subNote}`;
        } else if (nextTier) {
          const needed = nextTier.min - itemCount;
          this.tiersMsg.textContent = `Add ${needed} more for ${nextTier.value}% off — currently saving ${currentTier && currentTier.value ? currentTier.value + '%' : '0%'}${subNote}`;
        } else {
          this.tiersMsg.textContent = `Max discount unlocked — ${currentTier.value}% off${subNote}!`;
        }
      }
    }

    updateTotals(items, accessories) {
      const allItems = [...items, ...accessories];
      if (allItems.length === 0) {
        if (this.sidebarTotals) this.sidebarTotals.style.display = 'none';
        return;
      }

      if (this.sidebarTotals) this.sidebarTotals.style.display = '';

      const subtotal = allItems.reduce((sum, i) => sum + (i.price * i.quantity), 0);
      const compareTotal = allItems.reduce((sum, i) => sum + (i.comparePrice * i.quantity), 0);

      // Bundle tier discount (applied via discount code at checkout)
      const bundleDiscountPercent = this.getDiscountPercent();
      const bundleSubtotal = items.reduce((sum, i) => sum + (i.price * i.quantity), 0);
      const bundleDiscountAmount = Math.round(bundleSubtotal * bundleDiscountPercent / 100);
      const afterBundleDiscount = bundleSubtotal - bundleDiscountAmount;

      // Subscription discount (applied via selling plan on variant price)
      const isSubscription = this.state.purchaseMode === 'recurring';
      const subDiscountAmount = isSubscription ? Math.round(afterBundleDiscount * SUBSCRIPTION_DISCOUNT_PERCENT / 100) : 0;

      const accessoryTotal = accessories.reduce((sum, i) => sum + (i.price * i.quantity), 0);
      const total = afterBundleDiscount - subDiscountAmount + accessoryTotal;

      if (this.sidebarSubtotal) this.sidebarSubtotal.textContent = this.formatMoney(subtotal);

      if (compareTotal > subtotal && this.sidebarCompareRow) {
        this.sidebarCompareRow.style.display = '';
        if (this.sidebarCompare) this.sidebarCompare.textContent = this.formatMoney(compareTotal);
      } else if (this.sidebarCompareRow) {
        this.sidebarCompareRow.style.display = 'none';
      }

      // Bundle discount row
      if (bundleDiscountAmount > 0 && this.sidebarSavingsRow) {
        this.sidebarSavingsRow.style.display = '';
        if (this.sidebarSavings) this.sidebarSavings.textContent = `-${this.formatMoney(bundleDiscountAmount)}`;
      } else if (this.sidebarSavingsRow) {
        this.sidebarSavingsRow.style.display = 'none';
      }

      // Subscription discount row
      if (subDiscountAmount > 0 && this.sidebarSubRow) {
        this.sidebarSubRow.style.display = '';
        if (this.sidebarSubSavings) this.sidebarSubSavings.textContent = `-${this.formatMoney(subDiscountAmount)}`;
      } else if (this.sidebarSubRow) {
        this.sidebarSubRow.style.display = 'none';
      }

      if (this.sidebarTotal) this.sidebarTotal.innerHTML = `<strong>${this.formatMoney(total)}</strong>`;
    }

    updateMobileBar() {
      const items = [...this.state.selectedItems.values()];
      const accessories = [...this.state.accessoryItems.values()];
      const allItems = [...items, ...accessories];
      const count = allItems.reduce((sum, i) => sum + i.quantity, 0);

      const bundleDiscountPercent = this.getDiscountPercent();
      const bundleSubtotal = items.reduce((sum, i) => sum + (i.price * i.quantity), 0);
      const bundleDiscountAmount = Math.round(bundleSubtotal * bundleDiscountPercent / 100);
      const afterBundleDiscount = bundleSubtotal - bundleDiscountAmount;

      const isSubscription = this.state.purchaseMode === 'recurring';
      const subDiscountAmount = isSubscription ? Math.round(afterBundleDiscount * SUBSCRIPTION_DISCOUNT_PERCENT / 100) : 0;

      const accessoryTotal = accessories.reduce((sum, i) => sum + (i.price * i.quantity), 0);
      const total = afterBundleDiscount - subDiscountAmount + accessoryTotal;

      if (this.mobileCount) this.mobileCount.textContent = count;
      if (this.mobileTotal) this.mobileTotal.textContent = this.formatMoney(total);
    }

    updateCtaState() {
      const hasItems = this.state.selectedItems.size > 0;
      const disabled = !hasItems || this.state.isSubmitting;

      if (this.sidebarCta) this.sidebarCta.disabled = disabled;
      if (this.mobileCta) this.mobileCta.disabled = disabled;

      // Update CTA text for step 2
      if (this.state.currentStep === 2) {
        const text = 'Checkout';
        if (this.sidebarCta) {
          const ctaText = this.sidebarCta.querySelector('.rb-sidebar__cta-text');
          if (ctaText) ctaText.textContent = text;
        }
        if (this.mobileCta) this.mobileCta.textContent = text;
      }
    }

    updateCardPrices() {
      const bundleDiscountPercent = this.getDiscountPercent();
      const isSubscription = this.state.purchaseMode === 'recurring';
      const subDiscountPercent = isSubscription ? SUBSCRIPTION_DISCOUNT_PERCENT : 0;
      const hasAnyDiscount = bundleDiscountPercent > 0 || subDiscountPercent > 0;

      document.querySelectorAll('.rb-card:not([data-is-accessory])').forEach(card => {
        const addLabel = card.querySelector('.rb-card__add-label');
        if (!addLabel || card.dataset.available === 'false') return;

        const price = parseInt(card.dataset.price, 10);
        if (!price) return;

        if (hasAnyDiscount) {
          // Apply bundle tier discount first, then subscription discount
          let discounted = price;
          if (bundleDiscountPercent > 0) {
            discounted = Math.round(discounted * (100 - bundleDiscountPercent) / 100);
          }
          if (subDiscountPercent > 0) {
            discounted = Math.round(discounted * (100 - subDiscountPercent) / 100);
          }
          addLabel.innerHTML = `+ Add ${this.formatMoney(discounted)} <s class="rb-card__original-price">${this.formatMoney(price)}</s>`;
        } else {
          addLabel.textContent = `+ Add ${this.formatMoney(price)}`;
        }
      });
    }

    /* ========== Purchase Mode ========== */
    setPurchaseMode(mode) {
      this.state.purchaseMode = mode;

      this.toggleButtons.forEach(btn => {
        const isActive = btn.dataset.mode === mode;
        btn.classList.toggle('rb-toggle__btn--active', isActive);
        btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });

      if (this.frequencyRow) {
        this.frequencyRow.style.display = mode === 'onetime' ? 'none' : '';
      }

      // Hide OTP-only products (e.g. Gummies) when subscription is selected, show when one-time
      const isRecurring = mode === 'recurring';
      this.allCards.forEach(card => {
        if (ONETIME_ONLY_PRODUCT_IDS.includes(card.dataset.productId)) {
          card.style.display = isRecurring ? 'none' : '';
          // Remove from selection if switching to subscription
          if (isRecurring) {
            const variantId = card.dataset.variantId;
            if (this.state.selectedItems.has(variantId)) {
              this.state.selectedItems.delete(variantId);
              card.classList.remove('rb-card--selected');
              this.updateCardQtyDisplay(card, 0);
            }
          }
        }
      });

      this.matchSellingPlan();
      this.updateUI();
      this.emitEvent('rb:purchaseModeChange', { mode });
    }

    /* ========== Step Transitions ========== */
    transitionToStep2() {
      this.state.currentStep = 2;

      // Fade out step 1 sections
      if (this.sidebarToggle) this.sidebarToggle.style.display = 'none';
      this.categorySections.forEach(s => s.classList.add('rb-step-fade-out'));

      // After fade out, hide step 1 and fade in step 2
      setTimeout(() => {
        this.categorySections.forEach(s => {
          s.classList.remove('rb-step-fade-out');
          s.classList.add('rb-step-hidden');
        });

        // Add class to body to signal step 2 is active (for mobile CSS)
        document.body.classList.add('rb-step-2-active');

        if (this.accessoriesSection) {
          const inner = this.accessoriesSection.querySelector('.rb-accessories');
          if (inner) inner.style.display = '';
          this.accessoriesSection.classList.remove('rb-step-hidden');
          this.accessoriesSection.classList.add('rb-step-fade-in');

          // Scroll to top - on mobile scroll window, on desktop scroll left column
          if (window.innerWidth < 990) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            var leftCol = document.querySelector('.rb-page-grid__left');
            if (leftCol) leftCol.scrollTop = 0;
          }

          setTimeout(() => {
            this.accessoriesSection.classList.remove('rb-step-fade-in');
          }, 400);
        }
      }, 300);

      this.updateCtaState();
      this.emitEvent('rb:stepChange', { step: 2 });
    }

    transitionToStep1() {
      this.state.currentStep = 1;

      // Fade out step 2
      if (this.accessoriesSection) {
        this.accessoriesSection.classList.add('rb-step-fade-out');
      }

      setTimeout(() => {
        // Hide step 2
        if (this.accessoriesSection) {
          this.accessoriesSection.classList.remove('rb-step-fade-out');
          const inner = this.accessoriesSection.querySelector('.rb-accessories');
          if (inner) inner.style.display = 'none';
        }

        // Remove step 2 class from body
        document.body.classList.remove('rb-step-2-active');

        // Fade in step 1 sections
        if (this.sidebarToggle) this.sidebarToggle.style.display = '';
        this.categorySections.forEach(s => {
          s.classList.remove('rb-step-hidden');
          s.classList.add('rb-step-fade-in');
        });

        setTimeout(() => {
          this.categorySections.forEach(s => s.classList.remove('rb-step-fade-in'));
        }, 400);
      }, 300);

      this.updateCtaState();
      this.emitEvent('rb:stepChange', { step: 1 });
    }

    /* ========== CTA / Cart / Checkout ========== */
    async handleCtaClick() {
      if (this.state.isSubmitting) return;

      if (this.state.currentStep === 1) {
        await this.addBundleToCart();
      } else {
        await this.handleCheckout();
      }
    }

    async addBundleToCart() {
      if (this.state.selectedItems.size === 0) return;

      this.setSubmitting(true);

      try {
        // Step 1: Create Loop transaction
        let txnId = null;
        if (this.state.loopBundle) {
          txnId = await this.createTransaction();
        }

        // Step 2: Add items to Shopify cart
        const planId = parseInt(this.state.sellingPlanShopifyId, 10) || null;
        const cannotSubscribe = [];
        const items = Array.from(this.state.selectedItems.values()).map(item => {
          const cartItem = {
            id: parseInt(item.variantId, 10),
            quantity: item.quantity,
            properties: {}
          };

          if (txnId) {
            cartItem.properties._bundleId = txnId;
          }

          if (this.state.purchaseMode === 'recurring' && planId && item.hasSellingPlans
              && !ONETIME_ONLY_PRODUCT_IDS.includes(item.productId)) {
            // Only attach a plan the variant actually offers. /cart/add.js rejects the
            // WHOLE items array with a 422 "Cannot apply selling plan to variant" if any
            // single line names a plan it cannot take, so one product missing from the
            // bundle's selling plan group takes down every bundle containing it.
            // An empty list means the markup predates data-selling-plan-ids (cached HTML,
            // or the section not yet re-rendered). Fall back to the old behaviour there
            // rather than blocking every bundle. The case this guards against still lands:
            // a variant with real allocations that simply do not include the bundle plan.
            const allocations = item.sellingPlanIds || [];
            if (allocations.length === 0 || allocations.includes(planId)) {
              cartItem.selling_plan = planId;
            } else {
              cannotSubscribe.push(item.title || item.handle);
            }
          }

          return cartItem;
        });

        if (cannotSubscribe.length) {
          const names = cannotSubscribe.join(', ');
          const err = new Error(
            names + (cannotSubscribe.length > 1 ? ' are' : ' is') +
            ' not available on a subscription yet. Remove ' +
            (cannotSubscribe.length > 1 ? 'them' : 'it') + ' or switch to One-Time.'
          );
          err.userFacing = true;
          throw err;
        }

        // Add free brush with subscription bundle (if in stock)
        if (this.state.purchaseMode === 'recurring') {
          const giftBanner = document.getElementById('rb-free-gift-banner');
          if (giftBanner) {
            const brushVariantId = giftBanner.dataset.brushVariantId;
            if (brushVariantId) {
              items.push({ id: parseInt(brushVariantId, 10), quantity: 1 });
            }
          }
        }

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.description || 'Failed to add items to cart');
        }

        // Apply Loop bundle discount code to cart session
        await this.applyBundleDiscountCode();

        // Move to step 2 (accessories)
        this.transitionToStep2();
      } catch (err) {
        console.error('[RitualBundle] Error adding to cart:', err);
        alert(err && err.userFacing
          ? err.message
          : 'Something went wrong adding your bundle to cart. Please try again.');
      } finally {
        this.setSubmitting(false);
      }
    }

    async handleCheckout() {
      if (this.state.isSubmitting) return;

      this.setSubmitting(true);

      try {
        // Add any accessory items to cart (no bundle ID, no selling plan)
        if (this.state.accessoryItems.size > 0) {
          const accItems = Array.from(this.state.accessoryItems.values()).map(item => ({
            id: parseInt(item.variantId, 10),
            quantity: item.quantity
          }));

          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: accItems })
          });

          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw new Error(errData.description || 'Failed to add accessories to cart');
          }
        }

        // Apply latest bundle discount code (tier may have changed with accessories)
        await this.applyBundleDiscountCode();

        // Redirect to checkout, preserving URL params (fbclid, utm, etc.)
        const discount = this.getDiscount();
        const currentParams = new URLSearchParams(window.location.search);
        const checkoutParams = new URLSearchParams();
        ['fbclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(key => {
          if (currentParams.has(key)) checkoutParams.set(key, currentParams.get(key));
        });
        if (discount && discount.code) {
          checkoutParams.set('discount', discount.code);
        }
        const paramString = checkoutParams.toString();
        window.location.href = paramString ? '/checkout?' + paramString : '/checkout';
      } catch (err) {
        console.error('[RitualBundle] Error during checkout:', err);
        alert('Something went wrong. Please try again.');
        this.setSubmitting(false);
      }
    }

    setSubmitting(isSubmitting) {
      this.state.isSubmitting = isSubmitting;

      [this.sidebarCta, this.mobileCta].forEach(btn => {
        if (!btn) return;
        btn.disabled = isSubmitting;
      });

      // Toggle loading spinner on sidebar CTA
      if (this.sidebarCta) {
        const text = this.sidebarCta.querySelector('.rb-sidebar__cta-text');
        const loading = this.sidebarCta.querySelector('.rb-sidebar__cta-loading');
        if (text) text.style.display = isSubmitting ? 'none' : '';
        if (loading) loading.style.display = isSubmitting ? 'inline-flex' : 'none';
      }
    }

    /* ========== Mobile Scroll to Top ========== */
    scrollToSidebar() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    initMobileBarVisibility() {
      var mobileBar = document.getElementById('rb-mobile-bar');
      var sidebarCta = document.getElementById('rb-sidebar-cta');
      if (!mobileBar || !sidebarCta) return;

      // Hide by default, show only when CTA scrolls out of view
      mobileBar.style.transform = 'translateY(100%)';

      var observer = new IntersectionObserver(function(entries) {
        var ctaVisible = entries[0].isIntersecting;
        mobileBar.style.transform = ctaVisible ? 'translateY(100%)' : 'translateY(0)';
      }, { threshold: 0 });

      observer.observe(sidebarCta);
    }

    /* ========== Helpers ========== */
    getTotalItemCount() {
      let count = 0;
      for (const item of this.state.selectedItems.values()) {
        count += item.quantity;
      }
      return count;
    }

    formatMoney(cents) {
      if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
      const amount = (cents / 100).toFixed(2);
      return MONEY_FORMAT.replace('{{amount}}', amount);
    }

    escapeHtml(str) {
      if (!str) return '';
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    emitEvent(name, detail) {
      document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
    }
  }

  /* ========== Initialize on DOM Ready ========== */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new RitualBundleBuilder());
  } else {
    new RitualBundleBuilder();
  }
})();
